import {
  approximateCountResponseSchema,
  authorizationRequestResponseSchema,
  authorizationResponseSchema,
  instanceLevelMappingSchema,
  instanceTypeMappingSchema,
  jiraInstanceSchema,
  jiraIssueTypeSchema,
  jiraProjectModelSchema,
  jiraProjectTypesAndStatusesModelSchema,
  mappingFieldsSchema,
  validateJqlResponseSchema,
} from '@tempo-io/work-integration-dto';
import { z } from 'zod';
import { tempoApi } from '@/common/api/tempoApi';
import type {
  JiraCustomFieldFilter,
  JiraOauthRequestResponse,
  ValidateJqlRequest,
} from '@/common/api/types/workIntegrations';

const getInstances = async () => {
  const response = await tempoApi().get('api/v1/work-integration/instances').json();
  return z.array(jiraInstanceSchema).parse(response);
};

const getJiraInstanceIssueTypes = async ({ instanceId }: { instanceId: string }) => {
  const response = await tempoApi()
    .get(`api/v1/work-integration/instances/${instanceId}/issue-types`)
    .json();
  return z.array(jiraIssueTypeSchema).parse(response);
};

const getJiraIssueTypesAndStatuses = async ({
  projectIdOrKey,
  cloudId,
}: {
  cloudId: string;
  projectIdOrKey: string;
}) => {
  const response = await tempoApi()
    .get(`api/v1/work-integration/instances/${cloudId}/projects/${projectIdOrKey}`)
    .json();
  return jiraProjectTypesAndStatusesModelSchema.parse(response);
};

const getJiraIssueTypesWithHierarchy = async ({
  projectIdOrKey,
  cloudId,
}: {
  cloudId: string;
  projectIdOrKey: string;
}) => {
  const response = await tempoApi()
    .get(`api/v1/work-integration/instances/${cloudId}/projects/${projectIdOrKey}/issue-types`)
    .json();
  return z.array(jiraIssueTypeSchema).parse(response);
};

const getJiraProjects = async ({ cloudId }: { cloudId: string }) => {
  const response = await tempoApi()
    .get(`api/v1/work-integration/instances/${cloudId}/projects`)
    .json();
  return z.array(jiraProjectModelSchema).parse(response);
};

const getJiraFields = async ({
  instanceId,
  projectId,
}: {
  instanceId: string;
  projectId?: string;
}) => {
  const base = `api/v1/work-integration/instances/${instanceId}/fields`;
  const params = projectId ? `?projectId=${projectId}` : '';

  const response = await tempoApi().get(`${base}${params}`).json();
  return mappingFieldsSchema.parse(response);
};

const getJiraWorkItemCount = async ({
  projectIdOrKey,
  cloudId,
  typeIds,
  statusIds,
  includeWorkAfter,
  jql,
  customFieldFilters,
}: {
  cloudId: string;
  projectIdOrKey: string;
  typeIds?: string[];
  statusIds?: string[];
  includeWorkAfter?: string;
  jql?: string;
  customFieldFilters?: JiraCustomFieldFilter[];
}) => {
  const response = await tempoApi()
    .post(
      `api/v1/work-integration/instances/${cloudId}/projects/${projectIdOrKey}/approximate-count`,
      {
        json: {
          filters: {
            typeIds,
            statusIds,
            includeWorkAfter,
            jql,
            customFieldFilters,
          },
        },
      },
    )
    .json();
  return approximateCountResponseSchema.parse(response).count;
};

const validateJiraJql = async ({ cloudId, jql }: { cloudId: string; jql: string }) => {
  const response = await tempoApi()
    .post(`api/v1/work-integration/instances/${cloudId}/jql/validate`, {
      json: { jql } as ValidateJqlRequest,
    })
    .json();
  return validateJqlResponseSchema.parse(response);
};

const jiraOAuthRequest = async ({
  returnUrl,
  instanceId,
}: {
  returnUrl: string;
  instanceId?: string;
}): Promise<JiraOauthRequestResponse> => {
  const response = await tempoApi()
    .post('api/v1/work-integration/jira/oauth-client/authorization-request', {
      json: { returnUrl, instanceId },
    })
    .json();
  return authorizationRequestResponseSchema.parse(response);
};

const jiraOAuthResponse = async ({ returnUrl }: { returnUrl: string }) => {
  const response = await tempoApi()
    .post('api/v1/work-integration/jira/oauth-client/authorization-response', {
      json: { returnUrl },
    })
    .json();

  return authorizationResponseSchema.parse(response);
};

const getJiraItemLevelMappings = async ({ instanceId }: { instanceId: string }) => {
  const response = await tempoApi()
    .get(
      `api/v1/work-integration/integrations/jira/mappings/item-level-mappings?instanceId=${instanceId}`,
    )
    .json();
  const parsed = z.array(instanceLevelMappingSchema).parse(response);
  return parsed.map((m) => ({ ...m, instanceId }));
};

const getJiraItemTypeMappings = async ({ instanceId }: { instanceId: string }) => {
  const response = await tempoApi()
    .get(
      `api/v1/work-integration/integrations/jira/mappings/item-type-mappings?instanceId=${instanceId}`,
    )
    .json();
  const parsed = z.array(instanceTypeMappingSchema).parse(response);
  return parsed.map((m) => ({ ...m, instanceId }));
};

export const JiraIntegrationAPI = {
  getInstances,
  getJiraInstanceIssueTypes,
  getJiraFields,
  getJiraIssueTypesAndStatuses,
  getJiraIssueTypesWithHierarchy,
  getJiraProjects,
  getJiraWorkItemCount,
  validateJiraJql,
  jiraOAuthRequest,
  jiraOAuthResponse,
  getJiraItemLevelMappings,
  getJiraItemTypeMappings,
} as const;
